import React, { useState, useEffect } from "react";
import axios from "axios";
import Rating from "../components/rating";
import {
  DiReact,
  DiNodejsSmall,
  DiMongodb,
  DiPython,
  DiGit,
} from "react-icons/di";
import {
  SiJavascript,
  SiTypescript,
  SiDocker,
  SiAmazonwebservices,
  SiTailwindcss,
} from "react-icons/si";
import { FaServer, FaDatabase, FaCode, FaCloud } from "react-icons/fa";

import baseUrl from "../components/baseurl";

const Skills = () => {
  const [skillsData, setSkillsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const skillIcons = {
    React: <DiReact size={36} className="text-blue-500" />,
    "Node.js": <DiNodejsSmall size={36} className="text-green-600" />,
    JavaScript: <SiJavascript size={36} className="text-yellow-500" />,
    TypeScript: <SiTypescript size={36} className="text-blue-600" />,
    MongoDB: <DiMongodb size={36} className="text-green-500" />,
    Python: <DiPython size={36} className="text-blue-800" />,
    Git: <DiGit size={36} className="text-red-500" />,
    Docker: <SiDocker size={36} className="text-blue-700" />,
    AWS: <SiAmazonwebservices size={36} className="text-orange-500" />,
    "Tailwind CSS": <SiTailwindcss size={36} className="text-blue-400" />,
  };

  const getIconForSkill = (skill, category) => {
    const skillName = skill.toLowerCase();
    for (const [key, icon] of Object.entries(skillIcons)) {
      if (skillName.includes(key.toLowerCase())) {
        return icon;
      }
    }

    switch (category) {
      case "web development":
        return <FaCode size={36} className="text-indigo-600" />;
      case "software development":
        return <FaServer size={36} className="text-purple-600" />;
      case "version control":
        return <DiGit size={36} className="text-red-500" />;
      case "cloud operation":
        return <FaCloud size={36} className="text-blue-600" />;
      case "database":
        return <FaDatabase size={36} className="text-green-600" />;
      default:
        return <FaCode size={36} className="text-gray-600" />;
    }
  };

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await axios.get(`${baseUrl}/skillsget`);
        setSkillsData(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching skills:", error);
        setIsLoading(false);
      }
    };

    fetchSkills();
  }, []);

  return (
    <div className="container mx-auto px-6 py-16">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-indigo-900 mb-4">My Skills</h2>
        <div className="h-1 w-24 bg-indigo-500 mx-auto"></div>
        <p className="mt-6 text-gray-600 max-w-2xl mx-auto">
          Here are the technologies and skills I've mastered throughout my
          journey as a developer.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-16">
          <div className="relative">
            <div className="h-16 w-16 rounded-full border-t-4 border-b-4 border-indigo-500 animate-spin"></div>
            <div className="absolute top-0 left-0 h-16 w-16 rounded-full border-t-4 border-b-4 border-indigo-200 animate-spin animate-pulse"></div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {skillsData?.map((skill) => (
              <div
                key={skill._id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden group"
              >
                <div className="p-6 border-b border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-4 transform transition-transform group-hover:scale-110">
                        {getIconForSkill(skill.skill, skill.category)}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 leading-tight">
                        {skill.skill}
                      </h3>
                    </div>
                    <div className="flex-shrink-0">
                      <Rating rate={skill.rank} />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 capitalize">
                    {skill.category}
                  </p>
                </div>
                <div className="px-6 py-4">
                  <div className="relative pt-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-100">
                          Proficiency
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-indigo-600">
                          {(skill.rank / 5) * 100}%
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-100">
                      <div
                        style={{ width: `${(skill.rank / 5) * 100}%` }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600 transition-all duration-500 ease-in-out group-hover:bg-indigo-500"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-indigo-50 rounded-2xl p-8 shadow-md">
              <div className="flex items-center mb-6">
                <FaCode size={24} className="text-indigo-600 mr-3" />
                <h3 className="text-2xl font-bold text-indigo-900">
                  Web Development
                </h3>
              </div>
              <div className="space-y-4">
                {skillsData
                  ?.filter((x) => x.category === "web development")
                  .map((skill) => (
                    <div
                      key={skill._id}
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg shadow-sm gap-3"
                    >
                      <span className="font-medium text-gray-800">
                        {skill.skill}
                      </span>
                      <Rating rate={skill.rank} showNumber={true} />
                    </div>
                  ))}
              </div>
            </div>

            <div className="bg-purple-50 rounded-2xl p-8 shadow-md">
              <div className="flex items-center mb-6">
                <FaServer size={24} className="text-purple-600 mr-3" />
                <h3 className="text-2xl font-bold text-purple-900">
                  Software Development
                </h3>
              </div>
              <div className="space-y-4">
                {skillsData
                  ?.filter((x) => x.category === "software development")
                  .map((skill) => (
                    <div
                      key={skill._id}
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg shadow-sm gap-3"
                    >
                      <span className="font-medium text-gray-800">
                        {skill.skill}
                      </span>
                      <Rating rate={skill.rank} showNumber={true} />
                    </div>
                  ))}
              </div>
            </div>

            <div className="bg-blue-50 rounded-2xl p-8 shadow-md">
              <div className="flex items-center mb-6">
                <FaCloud size={24} className="text-blue-600 mr-3" />
                <h3 className="text-2xl font-bold text-blue-900">
                  Cloud Operations
                </h3>
              </div>
              <div className="space-y-4">
                {skillsData
                  ?.filter((x) => x.category === "cloud operation")
                  .map((skill) => (
                    <div
                      key={skill._id}
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg shadow-sm gap-3"
                    >
                      <span className="font-medium text-gray-800">
                        {skill.skill}
                      </span>
                      <Rating rate={skill.rank} showNumber={true} />
                    </div>
                  ))}
              </div>
            </div>

            <div className="bg-red-50 rounded-2xl p-8 shadow-md">
              <div className="flex items-center mb-6">
                <DiGit size={24} className="text-red-600 mr-3" />
                <h3 className="text-2xl font-bold text-red-900">
                  Version Control
                </h3>
              </div>
              <div className="space-y-4">
                {skillsData
                  ?.filter((x) => x.category === "version control")
                  .map((skill) => (
                    <div
                      key={skill._id}
                      className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg shadow-sm gap-3"
                    >
                      <span className="font-medium text-gray-800">
                        {skill.skill}
                      </span>
                      <Rating rate={skill.rank} showNumber={true} />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Skills;
